import React, { useState, useRef } from 'react';
import styled from 'styled-components';

const DropZone = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.isDragging ? 'rgba(0, 0, 0, 0.7)' : 'transparent'};
  color: white;
  z-index: ${props => props.isDragging ? 10 : -1};
  pointer-events: ${props => props.isDragging ? 'auto' : 'none'};
  transition: background-color 0.3s ease;
`;

const DropMessage = styled.div`
  font-size: 24px;
  font-weight: bold;
  text-align: center;
  padding: 20px;
  border: 3px dashed white;
  border-radius: 10px;
  background-color: rgba(0, 0, 0, 0.5);
`;

const DragDropUpload = ({ setSvgCode }) => {
  const [isDragging, setIsDragging] = useState(false);
  const dropZoneRef = useRef(null);
  
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };
  
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };
  
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Only set isDragging to false if we're leaving the dropzone itself
    // not when leaving its children
    if (e.target === dropZoneRef.current) {
      setIsDragging(false);
    }
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      
      // Check if it's an SVG file
      if (file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg')) {
        const reader = new FileReader();
        reader.onload = (event) => {
          const content = event.target.result;
          if (content.includes('<svg')) {
            // Process the SVG to ensure it fits well in the editor
            processSvgContent(content);
          } else {
            alert('The dropped file does not contain valid SVG content.');
          }
        };
        reader.readAsText(file);
      } else {
        alert('Please drop an SVG file.');
      }
    }
  };
  
  const processSvgContent = (content) => {
    try {
      const parser = new DOMParser();
      const svgDoc = parser.parseFromString(content, 'image/svg+xml');
      const svgElement = svgDoc.querySelector('svg');
      
      if (svgElement) {
        // Ensure the SVG has width and height attributes
        if (!svgElement.hasAttribute('width') || !svgElement.hasAttribute('height')) {
          // Get viewBox values or set defaults
          let viewBox = svgElement.getAttribute('viewBox');
          let width = 400;
          let height = 400;
          
          if (viewBox) {
            const viewBoxValues = viewBox.split(' ').map(Number);
            if (viewBoxValues.length === 4) {
              width = viewBoxValues[2];
              height = viewBoxValues[3];
            }
          } else {
            // If no viewBox, create one
            viewBox = `0 0 ${width} ${height}`;
            svgElement.setAttribute('viewBox', viewBox);
          }
          
          // Set width and height attributes
          svgElement.setAttribute('width', width);
          svgElement.setAttribute('height', height);
        }
        
        // Ensure the SVG has a viewBox if it has width and height
        if (!svgElement.hasAttribute('viewBox') && 
            svgElement.hasAttribute('width') && 
            svgElement.hasAttribute('height')) {
          const width = svgElement.getAttribute('width');
          const height = svgElement.getAttribute('height');
          svgElement.setAttribute('viewBox', `0 0 ${width} ${height}`);
        }
        
        // Convert the SVG back to a string
        const serializer = new XMLSerializer();
        const processedSvg = serializer.serializeToString(svgDoc);
        
        // Update the SVG code
        setSvgCode(processedSvg);
      } else {
        alert('Invalid SVG structure.');
      }
    } catch (error) {
      console.error('Error processing SVG:', error);
      // If processing fails, use the original content
      setSvgCode(content);
    }
  };
  
  return (
    <DropZone 
      ref={dropZoneRef}
      isDragging={isDragging}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <DropMessage>
        Drop your SVG file here
      </DropMessage>
    </DropZone>
  );
};

export default DragDropUpload; 